import { z } from 'zod';
import { ChipSchema, PositionSchema, RuleViolation } from './squad.ts';

/** A single upcoming fixture for a club, with FPL's 1-5 difficulty rating. */
export const Fixture = z.object({
  gameweek: z.number().int(),
  opponent: z.string(),
  isHome: z.boolean(),
  difficulty: z.number().int().min(1).max(5),
});
export type Fixture = z.infer<typeof Fixture>;

/**
 * The manager's stated risk appetite for this request — `climb` favours
 * differentials and accepts variance, `protect` favours template picks and
 * defends a rank already earned, `balanced` weighs the scores alone. This is
 * the manager's declared intent, not a computed input: it never changes
 * `scorePlayer`'s arithmetic, only which advice the model favours.
 */
export const ObjectiveSchema = z.enum(['climb', 'protect', 'balanced']);
export type Objective = z.infer<typeof ObjectiveSchema>;

/**
 * What a *request* may ask for — `ObjectiveSchema`'s three values, plus
 * `auto`: let the manager's rank pick the objective instead of stating one.
 * `auto` is only ever a request. `resolveObjective` (services/objective.ts)
 * turns it into one of the three real objectives before anything downstream
 * — scoring, the prompt, the stored result — ever sees it.
 */
export const RequestedObjectiveSchema = z.enum(['climb', 'protect', 'balanced', 'auto']);
export type RequestedObjective = z.infer<typeof RequestedObjectiveSchema>;

/**
 * Per-player scoring produced deterministically from ingested FPL data. The
 * model reasons about these numbers; it does not invent them.
 */
export const PlayerAssessment = z.object({
  name: z.string(),
  position: PositionSchema,
  club: z.string(),
  price: z.number(),
  /** Points per game over recent fixtures, from FPL's `form`. */
  form: z.number(),
  totalPoints: z.number(),
  seasonMinutes: z.number().int().min(0),
  /** Mean difficulty of the next N fixtures — lower is easier. */
  fixtureOutlook: z.number(),
  nextFixtures: z.array(Fixture).default([]),
  /** Composite 0-100 score used to rank hold/sell candidates. */
  score: z.number().min(0).max(100),
  /** How much of the season's evidence `score` rests on, derived from
   * `gameweeksPlayed` at scoring time (see `scoreConfidence` in
   * services/scoring.ts) — 'low' this early in a season that a single haul
   * can still swing the composite, 'high' once there's enough of a sample
   * that it can't. Lets a consumer show "this is close/uncertain" without
   * re-deriving "it is early" from `seasonMinutes` itself. `assessPlayer`
   * always sets it; optional (not defaulted) purely so an `AnalysisResult`
   * stored before this field existed, or a caller building a bare assessment
   * literal without it, still parses. */
  scoreConfidence: z.enum(['low', 'medium', 'high']).optional(),
  flags: z
    .array(z.enum(['injured', 'doubtful', 'suspended', 'rotation_risk', 'suspension_risk', 'blank', 'double']))
    .default([]),
});
export type PlayerAssessment = z.infer<typeof PlayerAssessment>;

/**
 * A shortlisted player who came close to being `in` but wasn't picked —
 * enough of their own row (name, club, price, score) to make the comparison
 * legible on its own, not a bare name a manager has to go look up. See
 * `TransferSuggestion.alternatives` for how these get attached.
 */
export const TransferAlternative = z.object({
  name: z.string(),
  club: z.string(),
  price: z.number(),
  score: z.number().min(0).max(100),
});
export type TransferAlternative = z.infer<typeof TransferAlternative>;

export const TransferSuggestion = z.object({
  out: z.string(),
  in: z.string(),
  /** Net cost in tenths of a million; negative means money freed up. */
  netCost: z.number(),
  reasoning: z.string(),
  confidence: z.enum(['low', 'medium', 'high']),
  /**
   * Other shortlisted candidates within `CLOSE_ALTERNATIVE_THRESHOLD`
   * (apps/api/src/services/analysis.ts) of `in`'s score — computed in code
   * from the same shortlist `in` was constrained to, never by the model:
   * closeness is arithmetic, not judgment (see CLAUDE.md). Optional, and
   * left unset rather than `[]` when nothing was close, so a genuinely
   * clear-cut pick renders exactly as it always has, and an `AnalysisResult`
   * stored before this field existed still parses.
   */
  alternatives: z.array(TransferAlternative).optional(),
});
export type TransferSuggestion = z.infer<typeof TransferSuggestion>;

export const AnalysisResult = z.object({
  squadId: z.string(),
  gameweek: z.number().int(),
  generatedAt: z.string(),
  /** Model that produced the narrative parts. */
  model: z.string(),
  /** Squad legality, computed in code before the model is consulted. */
  validation: z.object({
    valid: z.boolean(),
    violations: z.array(RuleViolation),
  }),
  /** Two or three sentences a human actually reads first. */
  summary: z.string(),
  assessments: z.array(PlayerAssessment),
  captain: z.object({
    pick: z.string(),
    alternative: z.string().optional(),
    reasoning: z.string(),
  }),
  transfers: z.object({
    recommended: z.array(TransferSuggestion),
    /** Points hit incurred if all recommendations are taken. */
    pointsHit: z.number().int().min(0),
    holdReasoning: z.string().optional(),
  }),
  chip: z.object({
    recommended: ChipSchema.nullable(),
    reasoning: z.string(),
  }),
  /** Ordered player names: index 0 is the first substitute. */
  benchOrder: z.array(z.string()).default([]),
  warnings: z.array(z.string()).default([]),
  /** The objective this advice was produced under, so a stored analysis is
   * interpretable later without the original request. Never `auto` — this
   * records what was actually resolved and used, not what was asked for. */
  objective: ObjectiveSchema,
  /** One sentence on why `objective` is in force: the manager's own stated
   * choice, or the standing that `resolveObjective` picked it from. */
  objectiveReason: z.string(),
});
export type AnalysisResult = z.infer<typeof AnalysisResult>;

export const AnalysisRequest = z.object({
  /** How many upcoming gameweeks the advice should look ahead over. */
  horizon: z.number().int().min(1).max(8).default(3),
  /** Cap on transfers to consider; the model may still recommend fewer. */
  maxTransfers: z.number().int().min(0).max(15).default(2),
  /** Set true to let the model recommend taking a points hit. */
  allowHits: z.boolean().default(false),
  /** The manager's stated risk appetite, or `auto` to let their rank pick
   * it. Defaults to `balanced` so existing callers see no change in
   * behaviour. */
  objective: RequestedObjectiveSchema.default('balanced'),
  /** The manager's own mini-league to measure effective ownership against;
   * omitted leaves the analysis exactly as it is today. */
  leagueId: z.number().int().positive().optional(),
});
export type AnalysisRequest = z.infer<typeof AnalysisRequest>;
/** What a *caller* may send: every field has a `.default()`, so all of them
 * are optional before parsing. `AnalysisRequest` above is the parsed shape the
 * app actually handles, where the defaults have been applied and every field is
 * present — validate at the boundary, trust inside. */
export type AnalysisRequestInput = z.input<typeof AnalysisRequest>;
